// Genesis audience-id helpers. An Audience represents a visibility scope;
// the well-known PUBLIC audience id marks an entry as world-readable. All
// helpers operate on the `sharedWith: [Audience!]` shape genesis returns.
// Ported verbatim from workshop/utils/sharing.js, typed.

export const PUBLIC_AUDIENCE_ID = "0a546db5-32d9-4aa6-8959-625b6f155acc";

export interface Audience {
  id?: string | null;
  isPublic?: boolean | null;
  name?: { localized?: string | null; english?: string | null } | null;
}

export const PUBLIC_AUDIENCE: Audience = {
  id: PUBLIC_AUDIENCE_ID,
  isPublic: true,
  name: { localized: "Öffentlich", english: "Public" },
};

function audienceId(audience: Audience | string | null | undefined): string | undefined {
  if (typeof audience === "string") return audience;
  return audience?.id ?? undefined;
}

export function audienceIds(audiences: ReadonlyArray<Audience | string> | null | undefined): string[] {
  return (audiences ?? [])
    .map(audienceId)
    .filter((id): id is string => Boolean(id));
}

export function hasPublicAudience(audiences: ReadonlyArray<Audience | string> | null | undefined): boolean {
  return (audiences ?? []).some(
    (audience) => audienceId(audience) === PUBLIC_AUDIENCE_ID || (typeof audience !== "string" && audience?.isPublic === true),
  );
}

export function audienceRelations(audiences: ReadonlyArray<Audience | string> | null | undefined): Array<{ id: string }> {
  return audienceIds(audiences).map((id) => ({ id }));
}

export function sameAudienceIds(
  a: ReadonlyArray<Audience | string> | null | undefined,
  b: ReadonlyArray<Audience | string> | null | undefined,
): boolean {
  const left = [...new Set(audienceIds(a))].sort();
  const right = [...new Set(audienceIds(b))].sort();
  return left.length === right.length && left.every((id, i) => id === right[i]);
}

export function setPublicAudienceIds(
  audiences: ReadonlyArray<Audience | string> | null | undefined,
  shared: boolean,
): string[] {
  const ids = audienceIds(audiences).filter((id) => id !== PUBLIC_AUDIENCE_ID);
  if (shared) ids.push(PUBLIC_AUDIENCE_ID);
  return ids;
}

export function setPublicSharedWith(
  audiences: ReadonlyArray<Audience> | null | undefined,
  shared: boolean,
): Audience[] {
  const kept = (audiences ?? []).filter(
    (audience) => audienceId(audience) !== PUBLIC_AUDIENCE_ID && audience?.isPublic !== true,
  );
  return shared ? [...kept, PUBLIC_AUDIENCE] : kept;
}
